import { useState } from "react";
import { usePackName, useTaxWords } from "@/app/session";
import { Link } from "@tanstack/react-router";
import { Icon, InputText, type LucideIconName } from "@/deps/ui-library";
import { PageHeader } from "@/components/PageHeader";

interface HubItem {
  group: string;
  label: string;
  description: string;
  icon: LucideIconName;
  to: string;
}

const planned = (feature: string) => `/planned/${feature}`;

/** The only settings directory (ADR-0028 rule 4). The Settings menu mirrors it. */
const LOCALIZATION = "Localization";

const ITEMS: HubItem[] = [
  {
    group: "Company and books",
    label: "Company profile",
    description: "Legal name, PAN, address, fiscal year.",
    icon: "Building2",
    to: "/settings?tab=company",
  },
  {
    group: "Company and books",
    label: "Chart of accounts",
    description: "The ledger's structure.",
    icon: "ListTree",
    to: "/settings?tab=coa",
  },
  {
    group: "Company and books",
    label: "Numbering",
    description: "Gapless sequences per document type.",
    icon: "Hash",
    to: "/settings?tab=numbering",
  },
  {
    group: "Company and books",
    label: "Period control",
    description: "Lock closed months against new postings.",
    icon: "CalendarClock",
    to: "/accounting/periods",
  },
  {
    group: "Company and books",
    label: "Year-end close",
    description: "Checks, adjustments, approval and lock.",
    icon: "CalendarCheck",
    to: "/accounting/year-end",
  },
  {
    group: "Company and books",
    label: "Approval rules",
    description: "Which documents need sign-off, and from whom.",
    icon: "SquareCheckBig",
    to: "/settings/approval-rules",
  },
  {
    group: "Company and books",
    label: "Audit trail",
    description: "Every posting, approval and configuration change.",
    icon: "History",
    to: "/settings?tab=audit",
  },
  {
    group: "Documents",
    label: "Invoicing setup",
    description: "Invoice options and the footer.",
    icon: "Settings2",
    to: "/settings/invoicing",
  },
  {
    group: "Documents",
    label: "Document branding",
    description: "Templates, logo, colour and print toggles.",
    icon: "Palette",
    to: "/settings/branding",
  },
  {
    group: "Documents",
    label: "Email templates",
    description: "Invoice, statement and reminder wording.",
    icon: "Mail",
    to: "/settings/email-templates",
  },
  {
    group: LOCALIZATION,
    label: "Country pack",
    description: "Currency, calendar and formats.",
    icon: "Globe",
    to: "/settings/localization",
  },
  {
    group: LOCALIZATION,
    label: "Tax codes",
    description: "VAT categories and rates, from the pack.",
    icon: "Percent",
    to: "/settings?tab=tax",
  },
  {
    group: LOCALIZATION,
    label: "Withholding categories",
    description: "TDS categories and rates.",
    icon: "Banknote",
    to: "/settings?tab=tax",
  },
  {
    group: LOCALIZATION,
    label: "Payment options",
    description: "How customers can pay, and what invoices say.",
    icon: "Wallet",
    to: "/settings/invoicing",
  },
  {
    group: "Compliance",
    label: "Compliance",
    description: "Legally-ready checks and BIS 3.0 validation.",
    icon: "ShieldCheck",
    to: planned("compliance"),
  },
  {
    group: "Connections",
    label: "Integrations",
    description: "Connections, deliveries and what syncs.",
    icon: "Link2",
    to: planned("integrations"),
  },
  {
    group: "Reporting layer",
    label: "Account mapping",
    description: "Accounts to report tags.",
    icon: "Tags",
    to: "/reports/mapping",
  },
  {
    group: "Reporting layer",
    label: "Report templates",
    description: "NFRS, IFRS and company formats.",
    icon: "LayoutList",
    to: "/reports/templates",
  },
  {
    group: "Reporting layer",
    label: "Report tags",
    description: "Each framework's tags, as supplied.",
    icon: "BookOpen",
    to: "/settings?tab=tags",
  },
  {
    group: "Team and security",
    label: "Users",
    description: "Who can get in, and until when.",
    icon: "Users",
    to: "/settings?tab=users",
  },
  {
    group: "Team and security",
    label: "Roles and permissions",
    description: "What each role can see and do.",
    icon: "ShieldCheck",
    to: "/settings?tab=roles",
  },
  {
    group: "Account",
    label: "Subscription",
    description: "The plan, what's on it, and its users.",
    icon: "CreditCard",
    to: "/settings/subscription",
  },
];

const GROUPS = [...new Set(ITEMS.map((item) => item.group))];

export function SettingsHubPage() {
  const say = useTaxWords();
  const pack = usePackName();
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const found = ITEMS.filter(
    (item) =>
      say(item.label).toLowerCase().includes(q) || say(item.description).toLowerCase().includes(q),
  );

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Settings" />
      <InputText
        aria-label="Search settings"
        placeholder="Search settings"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      {found.length === 0 ? (
        <p className="text-muted-foreground text-sm">No settings match “{query}”.</p>
      ) : null}

      {GROUPS.map((group) => {
        const items = found.filter((item) => item.group === group);
        if (items.length === 0) return null;
        const heading = group === LOCALIZATION && pack ? `${group} · ${pack}` : group;
        return (
          <section key={group} aria-label={heading} className="flex flex-col gap-2">
            <h2 className="text-foreground m-0 text-sm font-semibold">{heading}</h2>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {items.map((item) => {
                const [to, search] = item.to.split("?");
                return (
                  <Link
                    key={say(item.label)}
                    to={to as never}
                    search={
                      search
                        ? (Object.fromEntries(new URLSearchParams(search)) as never)
                        : undefined
                    }
                    className="bg-card border-border hover:border-primary flex items-start gap-3 rounded-lg border p-3 no-underline"
                  >
                    <span className="bg-accent text-primary flex size-8 shrink-0 items-center justify-center rounded-md">
                      <Icon name={item.icon} size="sm" />
                    </span>
                    <span className="min-w-0">
                      <span className="text-foreground block text-sm font-medium">
                        {say(item.label)}
                      </span>
                      <span className="text-muted-foreground block text-xs">
                        {say(item.description)}
                      </span>
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
